import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Bookmark, ChevronDown, Save, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { savedFilterService } from '@/services/savedFilterService'

const QUERY_KEY = 'saved-filters'

// Pick, save, update, share or delete saved JQL filters. `current` is the loaded filter (if any).
const SavedFiltersMenu = ({ jql, current, onSelect }) => {
  const queryClient = useQueryClient()
  const { data } = useQuery(QUERY_KEY, savedFilterService.list)
  const filters = data?.data?.filters || []
  const mine = filters.filter(f => f.mine)
  const shared = filters.filter(f => !f.mine)
  const [dialog, setDialog] = useState(null) // { mode: 'create' | 'edit', name, description, is_shared }
  const [saving, setSaving] = useState(false)

  const refresh = () => queryClient.invalidateQueries(QUERY_KEY)
  const errorText = (err, fallback) => err.response?.data?.error || fallback

  const saveDialog = async () => {
    setSaving(true)
    try {
      const body = { name: dialog.name, description: dialog.description, is_shared: dialog.is_shared }
      const res = dialog.mode === 'create'
        ? await savedFilterService.create({ ...body, jql })
        : await savedFilterService.update(current.id, body)
      toast.success(dialog.mode === 'create' ? 'Filter saved' : 'Filter updated')
      setDialog(null)
      refresh()
      onSelect(res.data.filter)
    } catch (err) {
      toast.error(errorText(err, 'Could not save the filter'))
    } finally {
      setSaving(false)
    }
  }

  const saveQueryToCurrent = async () => {
    try {
      const res = await savedFilterService.update(current.id, { jql })
      toast.success(`Saved the query to "${current.name}"`)
      refresh()
      onSelect(res.data.filter)
    } catch (err) {
      toast.error(errorText(err, 'Could not update the filter'))
    }
  }

  const remove = async () => {
    if (!window.confirm(`Delete the filter "${current.name}"?`)) return
    try {
      await savedFilterService.remove(current.id)
      toast.success('Filter deleted')
      refresh()
      onSelect(null)
    } catch (err) {
      toast.error(errorText(err, 'Could not delete the filter'))
    }
  }

  const item = (f) => (
    <DropdownMenuItem key={f.id} onClick={() => onSelect(f)} className="flex-col items-start gap-0">
      <span className="flex items-center gap-1.5 text-sm">
        {f.name}
        {f.is_shared && f.mine && <Users className="h-3 w-3 text-muted-foreground" title="Shared" />}
      </span>
      {!f.mine && <span className="text-[11px] text-muted-foreground">by {f.owner?.name}</span>}
    </DropdownMenuItem>
  )
  const changed = current && current.jql !== jql

  return (
    <>
      <div className="flex items-center gap-2">
        {current?.mine && changed && (
          <Button size="sm" variant="outline" onClick={saveQueryToCurrent}>
            <Save className="h-4 w-4 mr-1.5" /> Save to "{current.name}"
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline">
              <Bookmark className="h-4 w-4 mr-1.5" /> Filters <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 max-h-96 overflow-y-auto">
            <DropdownMenuLabel>My filters</DropdownMenuLabel>
            {mine.length ? mine.map(item) : <p className="px-2 py-1 text-xs text-muted-foreground">None yet</p>}
            {shared.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Shared by teammates</DropdownMenuLabel>
                {shared.map(item)}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={!jql} onClick={() => setDialog({ mode: 'create', name: '', description: '', is_shared: false })}>
              <Save className="h-4 w-4 mr-2" /> Save current search as…
            </DropdownMenuItem>
            {current?.mine && (
              <>
                <DropdownMenuItem onClick={() => setDialog({ mode: 'edit', name: current.name, description: current.description || '', is_shared: current.is_shared })}>
                  <Users className="h-4 w-4 mr-2" /> Rename or share "{current.name}"
                </DropdownMenuItem>
                <DropdownMenuItem onClick={remove} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete "{current.name}"
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'create' ? 'Save filter' : 'Filter details'}</DialogTitle>
            <DialogDescription className="font-mono text-xs break-all">{dialog?.mode === 'create' ? jql : current?.jql}</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="filter-name">Name</Label>
                <Input id="filter-name" autoFocus maxLength={100} value={dialog.name} onChange={(e) => setDialog(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Open bugs in PC" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-description">Description (optional)</Label>
                <Input id="filter-description" maxLength={500} value={dialog.description} onChange={(e) => setDialog(d => ({ ...d, description: e.target.value }))} />
              </div>
              <label className="flex items-center justify-between gap-3 rounded-md border p-3">
                <span>
                  <span className="block text-sm font-medium">Share with everyone</span>
                  <span className="block text-xs text-muted-foreground">Teammates can use it; each person still only sees projects they have access to.</span>
                </span>
                <Switch checked={dialog.is_shared} onCheckedChange={(v) => setDialog(d => ({ ...d, is_shared: v }))} />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={saveDialog} disabled={saving || !dialog?.name?.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SavedFiltersMenu
